import {
  useAccessibleBusinesses,
  useCurrentAccessRole,
  useCurrentBusiness,
  useCurrentUser,
} from '@billx/api';
import {
  Box,
  Divider,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { CrownIcon, PlusIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDevice } from '@/hooks';

import { CreateBusinessModal } from './CreateBusinessModal';

export interface BusinessSwitcherProps {
  isCollapsed?: boolean;
}

export const BusinessSwitcher: React.FC<BusinessSwitcherProps> = ({
  isCollapsed = false,
}) => {
  const { currentUser, updateUserPreference } = useCurrentUser();
  const { currentAccessRole } = useCurrentAccessRole();
  const { currentBusiness } = useCurrentBusiness();
  const { isMobile } = useDevice();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  const { accessibleBusinesses } = useAccessibleBusinesses();

  // Sync local state with preferences store
  useEffect(() => {
    if (currentBusiness && currentBusiness.id !== selectedBusinessId) {
      setSelectedBusinessId(currentBusiness.id);
    }
  }, [currentBusiness, selectedBusinessId]);

  const handleBusinessSelection = async (newBusinessId: string) => {
    setAnchorEl(null); // Close popover

    setSelectedBusinessId(newBusinessId);

    // Update preferences store (which will update database as side effect)
    if (currentUser) {
      try {
        await updateUserPreference.mutateAsync({
          selectedBusinessId: newBusinessId,
        });
        navigate('/');
      } catch (error) {
        console.error('Error updating business:', error);
      }
    }
  };

  // Don't render if no businesses
  if (accessibleBusinesses.length === 0) {
    return null;
  }

  // Get business initials for mobile display
  const getBusinessInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  };

  const currentBusinessName =
    accessibleBusinesses.find(
      (b) => b.id === (currentBusiness?.id || selectedBusinessId)
    )?.name ||
    accessibleBusinesses[0]?.name ||
    '';

  if (!currentBusinessName) {
    return;
  }

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          cursor: 'pointer',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: isCollapsed ? 'none' : undefined, // Hide this specific trigger if collapsed
        }}
      >
        <Box
          my={0.5}
          py={0.5}
          px={1}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: 1,
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <Typography variant="subtitle2" fontWeight={400}>
            {currentBusinessName}
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            {currentAccessRole?.role === 'owner' && (
              <Tooltip title="Owner" placement="top">
                <Box
                  sx={{
                    height: 30,
                    width: 30,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'gold',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <CrownIcon size={14} strokeWidth={2} />
                </Box>
              </Tooltip>
            )}
            <Box
              sx={{
                height: 30,
                width: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <PlusIcon size={14} strokeWidth={2} />
            </Box>
          </Box>
        </Box>
      </Box>

      {isCollapsed && (
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            px: 0.5,
            py: 0.5,
            cursor: 'pointer',
            height: isMobile ? 32 : 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              bgcolor: 'action.hover',
              borderRadius: 1,
            },
          }}
        >
          <Tooltip title={currentBusinessName} placement="right">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                minHeight: 24,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                letterSpacing="0.5px"
                textAlign="center"
              >
                {getBusinessInitials(currentBusinessName)}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            width: 220,
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Box display="flex" flexDirection="column" gap={1}>
            {accessibleBusinesses.map((business) => (
              <MenuItem
                key={business.id}
                selected={
                  business.id === (currentBusiness?.id || selectedBusinessId)
                }
                onClick={() => handleBusinessSelection(business.id)}
                sx={{ borderRadius: 1, py: 1 }}
              >
                <Typography variant="body2" noWrap>
                  {business.name}
                </Typography>
              </MenuItem>
            ))}
          </Box>
          <Divider sx={{ my: 1 }} />
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setIsCreateModalOpen(true);
            }}
            sx={{ borderRadius: 1 }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              color="primary.main"
            >
              <PlusIcon size={16} />
              <Typography variant="body2" fontWeight={500}>
                New Business
              </Typography>
            </Box>
          </MenuItem>
        </Box>
      </Popover>

      <CreateBusinessModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </>
  );
};
